import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import StatusBadge, { URGENCY } from "@/components/StatusBadge";
import Timeline from "@/components/Timeline";
import FileUploader from "@/components/FileUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, MapPin, Lock, DollarSign, ShieldCheck, CheckCircle2, XCircle, CreditCard, Loader2, Download, Plus,
} from "lucide-react";
import { toast } from "sonner";

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const [wo, setWo] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [busy, setBusy] = useState(false);

  // admin: request estimates from multiple vendors
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [scheduledDate, setScheduledDate] = useState("");
  const [selectedReqId, setSelectedReqId] = useState(null);
  const [markup, setMarkup] = useState("");
  // vendor estimate builder
  const [estItems, setEstItems] = useState([{ description: "", amount: "" }]);
  const [estAttachment, setEstAttachment] = useState(null);
  // vendor completion photos
  const [completionPhotos, setCompletionPhotos] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/work-orders/${id}`);
    setWo(data);
  };
  useEffect(() => {
    load();
    if (role === "admin") api.get("/vendors/approved").then((r) => setVendors(r.data)).catch(() => {});
  }, [id, role]);

  // prefill the vendor's estimate builder from their own request
  useEffect(() => {
    const mine = wo?.my_request;
    if (mine?.items?.length) {
      setEstItems(mine.items.map((it) => ({ description: it.description, amount: String(it.amount) })));
      if (mine.attachment) setEstAttachment(mine.attachment);
    }
  }, [wo?.id, wo?.my_request?.status]);

  const estTotal = useMemo(() => estItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0), [estItems]);
  const chosenReq = useMemo(
    () => (wo?.estimate_requests || []).find((r) => r.id === selectedReqId) || null,
    [wo, selectedReqId]
  );
  const pmPrice = useMemo(() => {
    const base = chosenReq?.total != null ? chosenReq.total : 0;
    return (base + (parseFloat(markup) || 0)).toFixed(2);
  }, [markup, chosenReq]);

  const toggleVendor = (vid) =>
    setSelectedVendorIds((p) => (p.includes(vid) ? p.filter((x) => x !== vid) : [...p, vid]));

  const requestEstimates = async () => {
    if (!selectedVendorIds.length) return toast.error("Select at least one vendor");
    setBusy(true);
    try {
      await api.post(`/work-orders/${id}/request-estimates`, { vendor_ids: selectedVendorIds, scheduled_date: scheduledDate || null });
      toast.success("Estimate requests sent");
      setSelectedVendorIds([]);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const selectEstimate = async () => {
    if (!selectedReqId) return toast.error("Pick a vendor's estimate first");
    if (markup === "") return toast.error("Enter a markup");
    setBusy(true);
    try {
      await api.post(`/work-orders/${id}/select-estimate`, { request_id: selectedReqId, markup_amount: parseFloat(markup) });
      toast.success("Quote sent to PM");
      setSelectedReqId(null); setMarkup("");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitEstimate = async () => {
    const items = estItems
      .filter((it) => it.description.trim() && it.amount !== "")
      .map((it) => ({ description: it.description, amount: parseFloat(it.amount) }));
    if (!items.length) return toast.error("Add at least one line item with a description and amount");
    setBusy(true);
    try {
      await api.post(`/work-orders/${id}/estimate`, { items, attachment: estAttachment });
      toast.success("Estimate submitted");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const quoteResp = async (action) => {
    setBusy(true);
    try {
      await api.post(`/work-orders/${id}/quote-response`, { action });
      toast.success(action === "approve" ? "Quote approved" : "Quote rejected");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    try {
      await api.post(`/work-orders/${id}/pay`);
      toast.success("Payment successful (mock)");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const vendorStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/work-orders/${id}/vendor-status`, { status, photos: status === "completed" ? completionPhotos : [] });
      toast.success("Status updated");
      setCompletionPhotos([]);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const closeTicket = async () => {
    setBusy(true);
    try { await api.post(`/work-orders/${id}/close`); toast.success("Ticket settled"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!wo) return <DashboardShell><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-sunburst" /></div></DashboardShell>;

  return (
    <DashboardShell>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-bold text-ocean">{wo.ref}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${URGENCY[wo.urgency]?.cls}`}>{URGENCY[wo.urgency]?.label} urgency</span>
                </div>
                <h1 className="text-2xl font-display font-black text-slate-900 mt-2">{wo.service_type}</h1>
                <p className="text-slate-500 flex items-center gap-1.5 mt-1"><MapPin className="w-4 h-4" />{wo.property_address}</p>
              </div>
              <StatusBadge status={wo.status} testid="wo-detail-status" />
            </div>
            <p className="mt-4 text-slate-600 leading-relaxed">{wo.description}</p>
            {(role === "admin" || role === "vendor") && (
              <p className="mt-3 text-sm text-slate-400">PM: {wo.pm_company || wo.pm_name}</p>
            )}
            {wo.photos?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Request photos</p>
                <div className="flex gap-2 flex-wrap">
                  {wo.photos.map((p, i) => <Thumb key={i} path={p} />)}
                </div>
              </div>
            )}
            {wo.completion_photos?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Completion photos</p>
                <div className="flex gap-2 flex-wrap">
                  {wo.completion_photos.map((p, i) => <Thumb key={i} path={p} />)}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-display font-semibold text-slate-900 mb-4">Status Timeline</h3>
            <Timeline events={wo.timeline} />
          </div>
        </div>

        {/* Sidebar: role-based actions */}
        <div className="space-y-6">
          {/* ADMIN pricing */}
          {role === "admin" && (
            <div className="bg-[#0B192C] rounded-2xl p-6 text-white grain relative">
              <div className="relative">
                <p className="font-mono text-xs uppercase tracking-widest text-white/50 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" />Admin pricing — full visibility</p>

                {["submitted", "awaiting_estimate"].includes(wo.status) && (
                  <div className="mt-4 space-y-3">
                    <Label className="text-white/70">{wo.status === "submitted" ? "Request estimates from vendors" : "Invite more vendors to bid"}</Label>
                    <div className="space-y-1.5 max-h-44 overflow-y-auto no-scrollbar">
                      {vendors.filter((v) => !(wo.estimate_requests || []).some((r) => r.vendor_id === v.id)).map((v) => (
                        <button key={v.id} type="button" onClick={() => toggleVendor(v.id)} data-testid={`admin-vendor-option-${v.id}`}
                          className={`w-full flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${selectedVendorIds.includes(v.id) ? "border-sunburst bg-sunburst/15 text-white" : "border-white/15 bg-white/5 text-white/70"}`}>
                          <span>{v.name}</span>
                          {selectedVendorIds.includes(v.id) && <CheckCircle2 className="w-4 h-4 text-sunburst" />}
                        </button>
                      ))}
                      {vendors.filter((v) => !(wo.estimate_requests || []).some((r) => r.vendor_id === v.id)).length === 0 && <p className="text-xs text-white/40">No more approved vendors to invite.</p>}
                    </div>
                    {wo.status === "submitted" && (
                      <div>
                        <Label className="text-white/70 text-xs">Scheduled date (optional)</Label>
                        <Input type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="mt-1.5 bg-white/10 border-white/20 text-white" />
                      </div>
                    )}
                    <Button data-testid="admin-request-estimates-button" onClick={requestEstimates} disabled={busy || !selectedVendorIds.length} className="w-full bg-sunburst hover:bg-orange-600 text-white font-semibold disabled:opacity-40">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Estimates"}
                    </Button>
                  </div>
                )}

                {wo.status === "awaiting_estimate" && (wo.estimate_requests || []).length > 0 && (
                  <div className="mt-5 space-y-3" data-testid="admin-estimate-compare">
                    <p className="font-mono text-xs uppercase tracking-widest text-white/50">Compare estimates</p>
                    {(wo.estimate_requests || []).map((r) => {
                      const submitted = r.status === "submitted" || r.status === "selected";
                      const totals = (wo.estimate_requests || []).filter((x) => x.total != null).map((x) => x.total);
                      const isLowest = submitted && r.total != null && r.total === Math.min(...totals);
                      return (
                        <div key={r.id} data-testid={`estimate-card-${r.id}`}
                          className={`rounded-xl border p-3 ${selectedReqId === r.id ? "border-sunburst bg-sunburst/10" : "border-white/10 bg-white/5"}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-white text-sm">{r.vendor_name}</span>
                            {submitted
                              ? <span className="font-mono font-bold text-white">${(r.total ?? 0).toFixed(2)}{isLowest && <span className="ml-1 text-[10px] text-palm">LOWEST</span>}</span>
                              : <span className="text-xs text-amber-300">Awaiting bid</span>}
                          </div>
                          {submitted && (r.items || []).length > 0 && (
                            <div className="mt-2 divide-y divide-white/10 border-t border-white/10">
                              {r.items.map((it, i) => (
                                <div key={i} className="flex justify-between py-1 text-xs text-white/70">
                                  <span>{it.description}</span><span className="font-mono">${it.amount.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {submitted && r.attachment && (
                            <button onClick={() => downloadFile(r.attachment.storage_path, r.attachment.original_filename)} className="mt-2 flex items-center gap-1.5 text-xs text-ocean hover:text-white">
                              <Download className="w-3.5 h-3.5" />{r.attachment.original_filename}
                            </button>
                          )}
                          {submitted && (
                            <button onClick={() => setSelectedReqId(r.id)} data-testid={`select-estimate-${r.id}`}
                              className={`mt-2 w-full rounded-lg py-1.5 text-xs font-semibold transition-colors ${selectedReqId === r.id ? "bg-sunburst text-white" : "bg-white/10 text-white/80 hover:bg-white/20"}`}>
                              {selectedReqId === r.id ? "Selected" : "Pick this estimate"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {chosenReq && (
                      <div className="space-y-3 border-t border-white/10 pt-3">
                        <div>
                          <Label className="text-white/70 text-xs">Your markup ($) on {chosenReq.vendor_name}'s ${(chosenReq.total ?? 0).toFixed(2)}</Label>
                          <Input data-testid="admin-markup-input" type="number" min="0" step="0.01" value={markup} onChange={(e) => setMarkup(e.target.value)} className="mt-1.5 bg-white/10 border-white/20 text-white font-mono" placeholder="0.00" />
                        </div>
                        <div className="flex items-center justify-between rounded-xl bg-sunburst/15 border border-sunburst/30 px-4 py-3">
                          <span className="text-sm text-white/80">PM price (auto)</span>
                          <span data-testid="admin-computed-pm-price" className="font-mono text-xl font-bold text-sunburst">${pmPrice}</span>
                        </div>
                        <Button data-testid="admin-send-quote-button" onClick={selectEstimate} disabled={busy} className="w-full bg-sunburst hover:bg-orange-600 text-white font-semibold">
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Quote to PM"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {!["submitted", "awaiting_estimate"].includes(wo.status) && (
                  <div className="mt-4 space-y-2 font-mono">
                    {(wo.estimate_items || []).length > 0 && (
                      <div className="rounded-xl bg-white/5 border border-white/10 divide-y divide-white/10 mb-3">
                        {wo.estimate_items.map((it, i) => (
                          <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                            <span className="text-white/70">{it.description}</span>
                            <span className="text-white/90">${it.amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <Row label="Vendor cost" value={`$${wo.vendor_cost ?? "—"}`} />
                    <Row label="Markup" value={`$${wo.markup_amount ?? "—"}`} accent />
                    <div className="border-t border-white/10 my-2" />
                    <Row label="PM price" value={`$${wo.pm_price ?? "—"}`} big />
                    <p className="text-xs text-white/40 pt-2">Vendor: {wo.vendor_name}</p>
                    {wo.invoice_paid && <p className="text-xs text-emerald-400 pt-1 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Invoice paid</p>}
                  </div>
                )}
              </div>
            </div>
          )}

          {role === "admin" && ["completed"].includes(wo.status) && (
            <Button data-testid="admin-close-ticket-button" onClick={closeTicket} disabled={busy} className="w-full bg-[#0B192C] hover:bg-[#1E3E62] text-white">Close & Settle Ticket</Button>
          )}

          {/* PM pricing */}
          {role === "pm" && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" />Guaranteed Florida PM Rate</p>
              {wo.pm_price != null ? (
                <>
                  <p className="mt-3 font-mono text-4xl font-black text-slate-900">${wo.pm_price}</p>
                  <p className="text-sm text-slate-400">All-inclusive service rate</p>
                  {wo.status === "quote_sent" && (
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <Button data-testid="pm-approve-quote-button" onClick={() => quoteResp("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="w-4 h-4 mr-1" />Approve</Button>
                      <Button data-testid="pm-reject-quote-button" onClick={() => quoteResp("reject")} disabled={busy} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"><XCircle className="w-4 h-4 mr-1" />Reject</Button>
                    </div>
                  )}
                  {["approved", "accepted", "in_progress", "completed"].includes(wo.status) && !wo.invoice_paid && (
                    <Button data-testid="pm-pay-invoice-button" onClick={pay} disabled={busy} className="mt-4 w-full bg-sunburst hover:bg-orange-600 text-white font-semibold">
                      <CreditCard className="w-4 h-4 mr-1" />Pay ${wo.pm_price}
                    </Button>
                  )}
                  {wo.invoice_paid && <p className="mt-4 text-sm text-emerald-600 font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Invoice paid</p>}
                </>
              ) : (
                <p className="mt-3 text-slate-400 text-sm">A quote will appear here once our team prices your request.</p>
              )}
            </div>
          )}

          {/* Vendor payout + actions */}
          {role === "vendor" && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <p className="font-mono text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" />{wo.vendor_id === user.id ? "Your Contract Rate" : "Your Estimate"}</p>
              <p data-testid="vendor-payout-amount" className="mt-3 font-mono text-4xl font-black text-emerald-600">
                ${wo.vendor_id === user.id ? (wo.vendor_cost ?? "—") : (wo.my_request?.total != null ? wo.my_request.total.toFixed(2) : "—")}
              </p>
              <p className="text-sm text-slate-400">{wo.vendor_id === user.id ? "Your payout for this job" : "Your submitted estimate"}</p>

              {wo.status === "awaiting_estimate" && wo.my_request && wo.my_request.status !== "declined" && (
                <div className="mt-5 space-y-3" data-testid="vendor-estimate-builder">
                  <Label>Your estimate breakdown</Label>
                  {estItems.map((it, i) => (
                    <div key={i} className="flex gap-2">
                      <Input placeholder="Description (e.g. Labor, 2 hrs)" value={it.description}
                        data-testid={`estimate-item-desc-${i}`}
                        onChange={(e) => setEstItems((p) => p.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))}
                        className="flex-1" />
                      <Input placeholder="0.00" type="number" min="0" step="0.01" value={it.amount}
                        data-testid={`estimate-item-amount-${i}`}
                        onChange={(e) => setEstItems((p) => p.map((x, idx) => idx === i ? { ...x, amount: e.target.value } : x))}
                        className="w-24 font-mono" />
                      {estItems.length > 1 && (
                        <button onClick={() => setEstItems((p) => p.filter((_, idx) => idx !== i))} className="text-slate-300 hover:text-red-500">
                          <XCircle className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={() => setEstItems((p) => [...p, { description: "", amount: "" }])} data-testid="estimate-add-line" className="text-sm text-ocean font-semibold flex items-center gap-1">
                    <Plus className="w-4 h-4" />Add line
                  </button>
                  <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                    <span className="text-sm text-slate-500">Estimate total</span>
                    <span data-testid="estimate-total" className="font-mono text-xl font-bold text-slate-900">${estTotal.toFixed(2)}</span>
                  </div>
                  <div>
                    <Label className="text-xs">Attach estimate (optional)</Label>
                    <div className="mt-1.5">
                      <FileUploader testid="estimate-attachment-upload" label="Attach PDF / photo estimate" accept="image/*,application/pdf"
                        onUploaded={(d) => setEstAttachment({ file_id: d.id, storage_path: d.storage_path, original_filename: d.original_filename })} />
                    </div>
                    {estAttachment && <p className="text-xs text-slate-400 mt-1 truncate">Attached: {estAttachment.original_filename}</p>}
                  </div>
                  <Button data-testid="vendor-submit-estimate-button" onClick={submitEstimate} disabled={busy} className="w-full bg-[#0B192C] hover:bg-[#1E3E62] text-white font-semibold">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (wo.my_request.status === "submitted" ? "Resubmit Estimate" : "Submit Estimate")}
                  </Button>
                  {wo.my_request.status === "submitted" && <p className="text-sm text-emerald-600">Estimate submitted — awaiting admin decision.</p>}
                </div>
              )}

              {wo.my_request?.status === "declined" && (
                <p className="mt-4 text-sm text-slate-500">Your estimate wasn't selected for this job.</p>
              )}

              {["approved", "accepted", "in_progress"].includes(wo.status) && wo.vendor_id === user.id && (
                <div className="mt-5 space-y-3">
                  <Label>Update status</Label>
                  <div className="grid gap-2">
                    {wo.status === "approved" && <Button data-testid="vendor-accept-job-button" onClick={() => vendorStatus("accepted")} disabled={busy} className="w-full bg-[#0B192C] hover:bg-[#1E3E62] text-white">Accept Job</Button>}
                    {wo.status === "accepted" && <Button data-testid="vendor-start-job-button" onClick={() => vendorStatus("in_progress")} disabled={busy} className="w-full bg-violet-600 hover:bg-violet-700 text-white">Start — In Progress</Button>}
                    {wo.status === "in_progress" && (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs">Completion photos</Label>
                          <div className="mt-1.5">
                            <FileUploader testid="vendor-photo-upload-input" label="Add before/after photos" accept="image/*" multiple
                              onUploaded={(d) => setCompletionPhotos((p) => [...p, d.storage_path])} />
                          </div>
                        </div>
                        <Button data-testid="vendor-complete-job-button" onClick={() => vendorStatus("completed")} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">Mark Completed</Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {wo.status === "quote_sent" && wo.vendor_id === user.id && <p className="mt-4 text-sm text-amber-600">Waiting for PM to approve the quote.</p>}
              {["completed", "closed"].includes(wo.status) && wo.vendor_id === user.id && <p className="mt-4 text-sm text-emerald-600 font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />Job complete</p>}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

const Row = ({ label, value, accent, big }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-white/60">{label}</span>
    <span className={`font-bold ${big ? "text-xl text-sunburst" : accent ? "text-white" : "text-white"}`}>{value}</span>
  </div>
);

const Thumb = ({ path }) => (
  <div className="relative group w-24 h-24">
    <img src={fileUrl(path)} alt="upload" className="w-24 h-24 object-cover rounded-lg border" />
    <button onClick={() => downloadFile(path, path.split("/").pop())} data-testid="download-photo-button"
      className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity">
      <Download className="w-5 h-5 text-white" />
    </button>
  </div>
);
